<script>
	import Scroller from '@sveltejs/svelte-scroller';
	import { Email, Reddit, LinkedIn, WhatsApp, Twitter } from 'svelte-share-buttons-component';
	import { csv } from 'd3-fetch';
	import Bubbles from "./Bubbles.svelte";
	import DetailCard from "./DetailCard.svelte";

	const url = 'https://objective-wiles-09a6fb.netlify.app/';
	const title = 'To All the Books I Read in 2020';
	const desc = 'A data visualization essay looking at the books I read and tracked on Goodreads in 2020.';

	// all these are for the scroller
	let index, offset, progress;

	// this controls the grouping for bubbles
	let view;

	// responsive width for bubble chart
	let width;
	let mobile;

	// store state of toggle btn
	let active = false;

	const scrollerVizId = "scroller-bubbles"
	const filterVizId = "filter-bubbles"
	const headerVizId = "header-bubbles"

	// this is reactive, so it runs whenever index changes
	$: if (index == 0) {
		view = "all"
	} else if (index == 1) {
		view="rating"
	} else if (index == 2) {
		view="genre"
	} else if (index == 3) {
		view="month"
	}

	$: if (width < 600) {
		mobile = true
	} else {
		mobile = false
	}

	// load the data
	let data;
	async function load() {
		const dataArr = [];
		data = await csv('data/goodreads.csv', d => { 
		  const returnObj = {
			title: d.title,
			author: d.fullname,
			pages: +d.pages,
			rating: +d.rating,
			month: d.month,
			review: d.review,
			genre: d.genre,
			topic: d.topics,
			cover: d.cover
		  };
		returnObj['dataArr'] = dataArr;
		return returnObj;
	});
  }
  load();
  let bookData;
  
  function handleClick(change) {
	view = change
  }

  // assign data from a bubble to bookData
  // to be passed down into DetailCard
  const showDetail = (e) => {
		bookData = e.detail
	}

</script>

<main>
	<div id={headerVizId} class="header-graphic" bind:clientWidth={width}></div>
		<div>
		{#if width}
			{#if data}
				<Bubbles current="intro" dataset={data} w={width} rootid={headerVizId}></Bubbles>
			{/if}
		{/if}
	</div>
	<div class='{mobile ? "mobile-top hero-title": "hero-title"}'>
		<h1>To all the books I read before (in 2020)</h1>
	</div>
	<article class="intro-text">
		<p style="font-style: italic; text-align: center; margin-bottom: 70px">a story by <a href="https://bendoesdataviz.com/" taret="_blank">Ben</a></p>
		<p>
			How to survive a year of isolation, social distancing, Zoom-fatigue, and doom scrolling?
		</p>
		<p>
			Books.
		</p>
		<p>At least that’s what I tried. In the miserable year of 2020 (may it rest fitfully), I turned to books as a quiet solace. I did not have the words to express what I wanted, needed, felt. So I turned to the words of others.</p>
		<p>In books we find a portal to new worlds. Some books help us to escape, while others hold up a mirror to our own world.</p>
		<aside>
			psst...anytime you see the <span class="glasses-icon">&#10175</span>, you can click to find the book on Goodreads!
		</aside>
		<p>I read <span class="pill" style="background-color: rgba(255, 145, 75,0.5)">memoirs</span> and <span class="pill" style="background-color: rgba(4, 70, 74, 0.3)">science fiction</span> and <span class="pill" style="background-color: rgba(154, 210, 218, 0.5)">graphic novels</span>. I learned about the <a target="_blank" href="https://www.goodreads.com/search?q=The%20Tea%20Girl%20of%20Hummingbird%20Lane"><span class="glasses-icon">&#10175</span>ancient tradition of tea brewing</a> in the mountain villages of China. I reveled in the <a arget="_blank" href="https://www.goodreads.com/book/show/40180098-the-overstory?from_search=true&from_srp=true&qid=BNCxu3Q35z&rank=1"><span class="glasses-icon">&#10175</span>magic of redwood forests</a>. I let my soul be swallowed by poems of grief, loss, and hope.</p>
		<p>If reading is an act of learning, then writing must be an act of remembering. I read some incredible books this year, ones that I hope to never forget. So this interactive essay will act as a commemoration, a bookend of sorts, but also a practice in remembering.</p>
		<p>To all the books I read last year: thank you. Your pages pulled me through.</p>
		{#if width < 800}
			<p style="margin-top: 100px; font-style: italic">Looks like you're using a mobile device! That's cool. This story is best experienced on desktop and tablet. Everything still works on mobile, although you won't see all the labels...but you do you!</p>
		{/if}
	</article>
	<Scroller top={0.2} bottom={0.8} bind:index bind:offset bind:progress>
		<div slot="background">
		  <!--
			This is the background content. It will stay fixed
			in place while the foreground scrolls over the top.
		  -->
		  <div id={scrollerVizId} bind:clientWidth={width}>
			{#if width}
				{#if data}
					<Bubbles current={view} dataset={data} w={width} rootid={scrollerVizId}></Bubbles>
				{/if}
			{/if}
			</div>
		</div>
	  
		<div slot="foreground">
			<section>
			<div class="scrolly-box">
				<div class="scrolly-text">
					<p>In 2020, I read a total of <span class="bold-text">40 books</span>.</p>
				</div>
			</div>	  
			</section>
		  <section>
			<div class="scrolly-box">
				<div class="scrolly-text">
					<p>For the most part, I read some very good books in 2020. This is what they look like sorted by my Goodreads rating. Over half received a 4&#10029 or 5&#10029.</p>
				</div>
			</div>
			</section>
		  <section>
			<div class="scrolly-box">
				<div class="scrolly-text">
					<p>I was not picky with genres. I wanted to read widely and find new ideas in undiscovered realms. Last year I read my first <span class="pill" style="background-color: rgba(154, 210, 218, 0.5)">graphic novel</span> and absolutely loved it (<a arget="_blank" href="https://www.goodreads.com/search?q=Blankets"><span class="glasses-icon">&#10175</span>Blankets</a>).</p>
				</div>
			</div>
		  </section>
		  <section>
			<div class="scrolly-box">
				<div class="scrolly-text">
					<p>Throughout the year, I read more or less consistently. Summer months were the slowest. I read the most books in the fall months of September and October.</p>
				</div>
			</div>
		  </section>
		</div>
	  </Scroller>

	  
	  <div class="section-divider">
		<h2 style="color: #F2BC51">Explore the books</h2>
		{#if mobile}
			<p style="color: white; font-size: 24px">
				Explore my reviews for each book below!
			</p>
		{:else}
		<p style="color: white; font-size: 24px">
			I reviewed every book. Click on a bubble to explore!
		</p>
		{/if}
	  </div>
	<div class='{mobile ? "hide": "container"}'>
		<p>Sort the books</p>
	<button on:click={() => handleClick("all")}>
		all
	</button>
	<button on:click={() => handleClick("month")}>
		month
	</button>
	<button on:click={() => handleClick("rating")}>
		rating
	</button>
	<button on:click={() => handleClick("genre")}>
		genre
	</button>

	
	  <div id={filterVizId} bind:offsetWidth={width}>
		{#if width}
			{#if data}
				<Bubbles on:bubHover={showDetail} current={view} dataset={data} w={width} rootid={filterVizId}></Bubbles>
			{/if}
		{/if}
		</div>
		<DetailCard book={bookData}></DetailCard>
	</div>
	
	<div>
		<button class:active={active} style="float: middle; margin-top: 20px" on:click="{() => active = !active}">
			{#if !active}
			&#10004 show all the books in a list?
			{:else}
			&#10008 hide full list below
			{/if}
		</button>
	</div>

	<div class="share-container">
		<h3>Give it a share</h3>
		<Email subject="{title}" body="{desc} {url}" />
		<Reddit class="share-button" {title} {url} />
		<LinkedIn class="share-button" {url} />
		<WhatsApp class="share-button" text="{title} {url}" />
		<Twitter class="share-button" text="{title}" {url} />
	</div>
	
	<!-- start book list -->
	<div>
		{#if data && active}
		<div class="flex-row">
			{#each data as book}
					<div class="flex-col">
						<DetailCard book={book}></DetailCard>
					</div>	
			{/each}
		</div>
		{/if}
	</div>
	<div class="footer">
		<div class="footer-text">
			<h2>Design notes</h2>
			<p>If ya know, ya know. If you don't, stop reading this and watch the beautifully designed and art-directed Netflix series "To All the Boys I've Loved Before". Then come back. Trust. That was pretty much my design process.</p>
		</div>
		<div class="footer-text">
			<h2>Dev notes</h2>
			<p>This was my first project using <a href="https://svelte.dev/" target="_blank">Svelte</a> from start to finish, and wow! Loved it. There are many places it could be more Svelte-y, but for a starter project the development process was very smooth. Svelte made the scrolly transitions easy with a native scroller plugin. The bubbles are some D3 force shenanigans inspired by <a target="_blank" href="https://bl.ocks.org/SpaceActuary/d6b5ca8e5fb17842d652d0de21e88a05">this block</a>.</p>
		</div>
	</div>
</main>

<style>	
	section { height: 80vh; margin-bottom: 100px }

	html, body {
		max-width: 100%;
		overflow-x: none;
	}

	.scrolly-box {
		max-width: 450px;
		background-color: white;
		opacity: 0.95;
		box-shadow: 2px 2px 4px #dedede;
		border: 1px solid #888;
		margin: 0 auto;
	}

	.scrolly-text {
		margin: 10px auto;
		text-align: center;
		padding: 10px;
		line-height: 1.6;
	}

	.intro-text {
		max-width: 600px;
		margin: 0 auto;
		margin-top: 150px;
		margin-bottom: 200px;
		text-align: left;
		line-height: 1.6;
		padding: 10px
	}

	.bold-text {
		font-weight: 600
	}

	.share-container {
		float: right; 
		margin-right: 30px;
		margin-top: 5px;
		margin-bottom: 30px;
	}

	.share-container h3 {
		font-family: "Rock Salt", cursive;
	}

	main {
		text-align: center;
		/* max-width: 240px; */
		margin: auto;
	}

	h1 {
		color: black;
		text-transform: uppercase;
		font-size: 5vw;
		font-weight: 100;
		font-family: 'Rock Salt', cursive;
	}

	.section-divider h2 {
		font-size: 5vw;
		text-transform: uppercase;
		font-family: 'Rock Salt', cursive;
	}

	.hero-title {
		background-color: rgba(255,255,255,0.6);
		margin: 0 auto;
		width: 100%;
		height: 500px;
		padding: 0px;
		position: absolute;
		top: 0;
		left: 0;
	}

	.mobile-top {
		height: 400px
	}

	.header-graphic {
		position: relative;
		max-width: 800px
	}

	p {
		font-size: 20px
	}

	#bubble-viz {
		z-index: 9999
	}

	.footer {
		background-color: #03454A;
		line-height: 1.4;
		text-align: left;
		width: 100%;
		padding: 10px;
		margin-top: 150px
	}

	.footer-text {
		margin: 0 auto;
		max-width: 500px
	}

	.footer-text p {
		padding: 10px;
		font-size: 16px;
		color: lightblue;
		max-width: 500px;
	}

	.footer-text h2 {
		padding: 10px;
		padding-bottom: 0px;
		color: lightblue;
	}

	.footer a {
		color: white;
		text-decoration: underline;
	}

	.footer a:hover {
		color: lightgray
	}

	@media (min-width: 640px) {
		main {
			max-width: none;
		}
	}

	@media (max-width: 800px) {
		h1 {
			font-size: 2em
		}

		.half-col {
			flex: 100%
		}
	}

	.flex-row {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		width: 100%;
	}

	.flex-col {
		display: flex;
		flex-direction: column;
		flex-basis: 100%;
		flex: 100%;
	}

	.hide {
		display: none
	}

	.container {
		display: block;
	}

	/* @media (max-width: 1200px) {
		.flex-row {
			display: block;
		}
		.flex-col {
			width: 100%;
		}
	} */

	.active {
		background-color: pink;
	}

	.section-divider {
		width: 100% !important;
		height: 420px;
		background-color:#00A8A7;
		margin-top: 20px;
		margin-bottom: 50px;
		padding: 20px 0px 20px 0px
	}

	aside {
		width: 30%;
		padding-left: .5rem;
		margin-left: .5rem;
		float: right;
		box-shadow: inset 6px 0 5px -5px #808080;
		font-style: italic;
		color: #888888;
	}

	.glasses-icon {
		padding-right: 4px;
		display:inline-block;
		font-size: 22px;
	}

	.pill {
		display: inline-block;
		padding: 0em 0.2em;
		text-align: center;
		border-radius: 0.2em;
		color: black;
	}

</style>